// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
    Address,
    AddressCanada,
    AddressUS,
    AuthenticatedPaymentEntryContextView,
    GuestPaymentEntryContextView,
    GuestProcessCardTokenPaymentRequest,
    ProcessCardTokenPaymentResponse,
    PublicResolvedPaymentRequestViewBase,
    PublicPaymentRequestView,
    CustomerPaymentMethodListItem,
} from '@pillar-portal/api-types';
import {
    AddressSchema,
    buildPaymentConfigurationDisplayValues,
    CardDetailsModel,
    CardType,
    CardFundingVerificationFailureKind,
    CanadaProvinceKeyList,
    CanadaProvinceSchema,
    classifyCardFundingVerificationFailure,
    formatTimestampForMerchantUi,
    normalizeCanadaPostalCode,
    normalizeUSZipCode,
    addLosslessNumbers,
    compareLosslessNumber,
    formatLosslessNumberFixed,
    formatLosslessUsd,
    HostedCardTokenMetadataTier,
    LosslessNumber,
    ReceiptEmailSchema,
    USStateKeyList,
    USStateSchema,
} from '@pillar-portal/api-types';
import { ApiError, paymentsApi } from '@/lib/pillarApi';
import { Button } from '@/components/ui/button';
import { formatPaymentRequestSourceDocumentReference } from '@/app/payment-requests/source-document-reference';
import { runInBackground } from '@/lib/utils';
import {
    SAVE_CARD_FOR_FASTER_CHECKOUT_LABEL,
    SAVE_CARD_FOR_FASTER_CHECKOUT_TOOLTIP,
} from '@/lib/save-card-copy';
import {
    CardFormData,
    resolveGatewayTokenizationCardBrand,
    resolveGatewayTokenizationPanPrefix,
    resolveGatewayTokenizationPaymentToken,
    resolveCardBrand,
    tokenizeCardWithDelegateToken,
    validateCardFormData,
} from '@/lib/card-payment-entry';
import {
    resolvePortalCardEntryAdapter,
    isPortalHostedCardEntryAdapter,
    type PortalHostedCardEntryTokenResult,
    type PortalHostedCardTokenResult,
} from '@/lib/ipg/card-entry';
import { FullyOwnedInvoiceCardEntry } from './FullyOwnedInvoiceCardEntry';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { HostedInvoiceCardEntry } from './HostedInvoiceCardEntry';

type LoadState = 'loading' | 'ready' | 'error';
const payLog = createComponentLogger('PublicPaymentPage');

type PaymentExecutionState = 'idle' | 'processing' | 'success' | 'error';
type PaymentExecutionErrorKind = 'decline' | 'processing';
type CardFundingVerificationState = 'idle' | 'loading' | 'resolved' | 'unavailable' | 'error';

interface PaymentExecutionErrorView {
    kind: PaymentExecutionErrorKind;
    title: string;
    body: string;
}

interface PaymentLinkActionNotice {
    tone: 'success' | 'error';
    title: string;
    body: string;
}

type PaymentLinkCardFormData = CardFormData & {
    billingAddress: Address;
};

const CARD_FUNDING_UNAVAILABLE_MESSAGE =
    'Unable to determine whether this card is credit or debit. Use another card or try again later.';
const CARD_FUNDING_VERIFICATION_FAILED_MESSAGE =
    'Unable to verify whether this card is credit or debit. Confirm the card number and try again.';
const CARD_FUNDING_SPECIFIC_FAILURE_PREFIX = 'Unable to verify card funding';

function resolveCardFundingVerificationFailure(error: unknown): {
    state: Extract<CardFundingVerificationState, 'unavailable' | 'error'>;
    message: string;
} {
    const failure = classifyCardFundingVerificationFailure(error);
    switch (failure.kind) {
        case CardFundingVerificationFailureKind.UNAVAILABLE:
            return {
                state: 'unavailable',
                message: CARD_FUNDING_UNAVAILABLE_MESSAGE,
            };
        case CardFundingVerificationFailureKind.SPECIFIC_FAILURE:
            return {
                state: 'error',
                message: `${CARD_FUNDING_SPECIFIC_FAILURE_PREFIX}: ${failure.message}`,
            };
        case CardFundingVerificationFailureKind.INVALID_BIN:
        case CardFundingVerificationFailureKind.GENERIC_FAILURE:
            return {
                state: 'error',
                message: CARD_FUNDING_VERIFICATION_FAILED_MESSAGE,
            };
    }
    throw new Error(`Unhandled card funding failure kind: ${failure.kind}`);
}

const createEmptyUSBillingAddress = (): Address => ({
    country: 'US',
    street: '',
    city: '',
    state: 'CA',
    zip: '',
});

function hasPaymentLinkBillingAddressInput(address: Address): boolean {
    if (address.street.trim().length > 0 || address.city.trim().length > 0) {
        return true;
    }
    if (address.street2?.trim() !== undefined && address.street2.trim().length > 0) {
        return true;
    }
    if (address.country === 'US') {
        return address.zip.trim().length > 0;
    }
    return address.postal_code.trim().length > 0;
}

function resolvePaymentLinkBillingAddress(cardData: PaymentLinkCardFormData): Address | undefined {
    if (!hasPaymentLinkBillingAddressInput(cardData.billingAddress)) {
        return undefined;
    }
    const parseResult = AddressSchema.safeParse(cardData.billingAddress);
    if (!parseResult.success) {
        throw new Error('Enter a valid billing address.');
    }
    return parseResult.data;
}

function normalizeOptionalReceiptEmail(value: string): string | undefined {
    const normalizedValue = value.trim();
    return normalizedValue.length === 0 ? undefined : normalizedValue;
}

function validateOptionalReceiptEmail(value: string): string | null {
    const normalizedValue = normalizeOptionalReceiptEmail(value);
    if (normalizedValue === undefined) {
        return null;
    }
    const parseResult = ReceiptEmailSchema.safeParse(normalizedValue);
    return parseResult.success ? null : 'Enter a valid receipt email address.';
}

function resolvePaymentAmounts(
    paymentEntryContext: GuestPaymentEntryContextView,
    cardType: CardType | null,
    hostedSurchargeAmount?: LosslessNumber
): Pick<
    GuestProcessCardTokenPaymentRequest,
    'surcharge_amount' | 'convenience_fee_amount' | 'total_fee_amount' | 'total_amount_paid'
> | null {
    if (paymentEntryContext.original_amount === undefined) {
        throw new Error('Payment request is missing original_amount.');
    }
    const displayValues = buildPaymentConfigurationDisplayValues({
        amount: paymentEntryContext.original_amount,
        cardType,
        allowSurcharge: paymentEntryContext.payment_configuration.allow_surcharge,
        allowConvenienceFee: paymentEntryContext.payment_configuration.allow_convenience_fee,
        surchargePercent: paymentEntryContext.payment_configuration.surcharge_pct,
        convenienceFee: paymentEntryContext.payment_configuration.convenience_fee,
    });
    if (
        displayValues.surchargeAmount === undefined ||
        displayValues.convenienceFeeAmount === undefined ||
        displayValues.totalFeeAmount === undefined ||
        displayValues.totalAmountPaid === undefined
    ) {
        return null;
    }
    const surchargeAmount = hostedSurchargeAmount ?? displayValues.surchargeAmount;
    const convenienceFeeAmount = displayValues.convenienceFeeAmount;
    const totalFeeAmount = addLosslessNumbers([surchargeAmount, convenienceFeeAmount]);
    return {
        surcharge_amount: surchargeAmount,
        convenience_fee_amount: convenienceFeeAmount,
        total_fee_amount: totalFeeAmount,
        total_amount_paid: addLosslessNumbers([
            paymentEntryContext.original_amount,
            totalFeeAmount,
        ]),
    };
}

function resolveStoredPaymentMethodCardType(
    paymentMethod: CustomerPaymentMethodListItem | null
): CardType | null {
    if (paymentMethod === null) {
        return null;
    }
    const details = paymentMethod.payment_method_token_details;
    if (details.payment_rail === 'DEBIT_CARD') {
        return details.card_type;
    }
    if (details.payment_rail === 'CREDIT_CARD') {
        return details.card_type;
    }
    return null;
}

function resolveStoredPaymentMethodDisplayValues(
    paymentMethod: CustomerPaymentMethodListItem | null
): PaymentLinkCardFormData | null {
    if (paymentMethod === null) {
        return null;
    }
    const details = paymentMethod.payment_method_token_details;
    if (details.payment_rail !== 'CREDIT_CARD' && details.payment_rail !== 'DEBIT_CARD') {
        return null;
    }

    return {
        cardNumber: details.masked_pan,
        expiryDate: 'XX/XX',
        cvv: 'XXXX',
        cardholderName: details.cardholder_name,
        billingAddress: createEmptyUSBillingAddress(),
    };
}

function validatePaymentLinkCardFormData(
    cardData: PaymentLinkCardFormData,
    addressRequiredForCardPayment: boolean
): string | null {
    const cardValidationError = validateCardFormData(cardData);
    if (cardValidationError !== null) {
        return cardValidationError;
    }
    if (!hasPaymentLinkBillingAddressInput(cardData.billingAddress)) {
        return addressRequiredForCardPayment ? 'Enter a valid billing address.' : null;
    }
    let billingAddress: Address | undefined;
    try {
        billingAddress = resolvePaymentLinkBillingAddress(cardData);
    } catch {
        return 'Enter a valid billing address.';
    }
    if (billingAddress === undefined) {
        return null;
    }
    if (billingAddress.street.trim().length === 0 || billingAddress.city.trim().length === 0) {
        return 'Enter the billing street and city.';
    }
    if (billingAddress.country === 'US' && billingAddress.zip === '00000') {
        return 'Enter a real customer billing ZIP.';
    }
    return null;
}

function isOptionalAuthenticatedPaymentEntryContextAuthorizationError(
    error: unknown
): error is ApiError {
    return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

function updateBillingAddress(
    address: Address,
    field: keyof AddressUS | keyof AddressCanada,
    value: string
): Address {
    if (address.country === 'US') {
        if (field === 'state') {
            const stateParseResult = USStateSchema.safeParse(value);
            return stateParseResult.success
                ? {
                      ...address,
                      state: stateParseResult.data,
                  }
                : address;
        }
        if (field === 'street' || field === 'street2' || field === 'city' || field === 'zip') {
            const resolvedValue: string = field === 'zip' ? normalizeUSZipCode(value) : value;
            return {
                ...address,
                [field]: resolvedValue,
            };
        }
        return address;
    }
    if (field === 'province') {
        const provinceParseResult = CanadaProvinceSchema.safeParse(value);
        return provinceParseResult.success
            ? {
                  ...address,
                  province: provinceParseResult.data,
              }
            : address;
    }
    if (field === 'street' || field === 'street2' || field === 'city' || field === 'postal_code') {
        const resolvedValue: string =
            field === 'postal_code' ? normalizeCanadaPostalCode(value) : value;
        return {
            ...address,
            [field]: resolvedValue,
        };
    }
    return address;
}

function changeBillingAddressCountry(address: Address, country: Address['country']): Address {
    if (country === address.country) {
        return address;
    }
    if (country === 'CA') {
        return {
            country: 'CA',
            street: address.street,
            street2: address.street2,
            city: address.city,
            province: 'ON',
            postal_code: '',
        };
    }
    return {
        country: 'US',
        street: address.street,
        street2: address.street2,
        city: address.city,
        state: 'CA',
        zip: '',
    };
}

function filterSelectableStoredPaymentMethods(
    paymentMethods: ReadonlyArray<CustomerPaymentMethodListItem>
): ReadonlyArray<CustomerPaymentMethodListItem> {
    return paymentMethods.filter((paymentMethod) => paymentMethod.status === 'ACTIVE');
}

function resolveErrorMessage(error: unknown): string {
    if (error instanceof ApiError && error.status === 401) {
        return 'This payment link is no longer active in this browser session.';
    }
    if (error instanceof Error && error.message.trim().length > 0) {
        return error.message;
    }
    return 'Unable to load the payment request.';
}

function resolvePaymentRequestLoadErrorMessage(error: unknown): string {
    if (error instanceof ApiError && error.status === 401) {
        return 'This payment link is no longer active in this browser session.';
    }
    return 'We could not load this payment request. Please try the link again or contact the sender.';
}

function buildPaymentDeclineErrorView(errorMessage: string | undefined): PaymentExecutionErrorView {
    const normalizedErrorMessage = errorMessage?.trim();
    return {
        kind: 'decline',
        title: 'Payment Declined',
        body:
            normalizedErrorMessage !== undefined && normalizedErrorMessage.length > 0
                ? `Card was rejected: ${normalizedErrorMessage}. Try a different card.`
                : 'Card was rejected. Try a different card.',
    };
}

function formatSurchargePercent(surchargePercent: LosslessNumber): string {
    return surchargePercent.value;
}

function buildPaymentProcessingErrorView(error: unknown): PaymentExecutionErrorView {
    const baseMessage = 'Something went wrong, your card was not charged. Please try again.';
    const errorMessage = resolveErrorMessage(error).trim();

    if (errorMessage.length === 0 || errorMessage === 'Unable to load the payment request.') {
        return {
            kind: 'processing',
            title: 'Processing Error',
            body: baseMessage,
        };
    }

    return {
        kind: 'processing',
        title: 'Processing Error',
        body: `${baseMessage} ${errorMessage}`,
    };
}

function formatCurrency(
    amount: LosslessNumber | undefined,
    currencyCode: string | undefined
): string {
    if (amount === undefined || currencyCode === undefined) {
        return 'Unavailable';
    }
    return currencyCode === 'USD'
        ? formatLosslessUsd(amount)
        : `${formatLosslessNumberFixed(amount, 2)} ${currencyCode}`;
}

function formatDateLabel(
    value: string | undefined,
    timeZone: PublicResolvedPaymentRequestViewBase['merchant_timezone']
): string {
    if (value === undefined) {
        return 'Unavailable';
    }
    return formatTimestampForMerchantUi(value, timeZone);
}

function isResolvedPaymentRequestView(
    view: PublicPaymentRequestView
): view is Exclude<PublicPaymentRequestView, { state: 'ERROR' }> {
    return view.state !== 'ERROR';
}

function renderViewHeading(view: PublicPaymentRequestView): {
    title: string;
    body: string;
} {
    switch (view.state) {
        case 'ACTIVE':
            return {
                title: view.title ?? 'Payment Request',
                body: 'Review the request details before continuing to payment.',
            };
        case 'EXPIRED':
            return {
                title: 'Payment Link Expired',
                body: 'This link has expired. You can request a new payment link to be sent.',
            };
        case 'CANCELED':
            return {
                title: 'Payment Link Not Valid',
                body: 'This payment link is not valid.',
            };
        case 'PAID':
            return {
                title: 'Payment Already Completed',
                body: 'This payment request has already been fulfilled.',
            };
        case 'REISSUED':
            return {
                title: 'Payment Link Replaced',
                body: 'This payment link was replaced by a newer one.',
            };
        case 'ERROR':
            return {
                title:
                    view.error_code === 'PAYMENT_REQUEST_LINK_INVALID'
                        ? 'Payment Link Not Valid'
                        : 'Payment Request Unavailable',
                body:
                    view.error_code === 'PAYMENT_REQUEST_LINK_INVALID'
                        ? 'This payment link is not valid.'
                        : 'This payment link is not available.',
            };
    }
}

function renderResolvedStateNote(view: Exclude<PublicPaymentRequestView, { state: 'ERROR' }>) {
    if (view.state === 'EXPIRED') {
        return (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Expired on {formatDateLabel(view.expires_ts, view.merchant_timezone)}
            </div>
        );
    }
    if (view.state === 'CANCELED') {
        return (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                This payment request was canceled by the merchant and can no longer be used.
            </div>
        );
    }
    if (view.state === 'PAID') {
        return (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                Paid on {formatDateLabel(view.fulfilled_ts, view.merchant_timezone)}
            </div>
        );
    }
    if (view.state === 'REISSUED') {
        return (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                {view.replacement_expires_ts !== undefined
                    ? `A replacement link is active until ${formatDateLabel(view.replacement_expires_ts, view.merchant_timezone)}.`
                    : 'A replacement link is currently active for this request.'}
            </div>
        );
    }
    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            Secure guest access established for this payment request.
        </div>
    );
}

function renderDocumentActions(view: Exclude<PublicPaymentRequestView, { state: 'ERROR' }>) {
    if (view.documents.length === 0) {
        return <div className="text-sm text-slate-500">No documents attached.</div>;
    }

    return (
        <div className="space-y-3">
            {view.documents.map((document) => (
                <div
                    key={document.payment_request_document_uuid}
                    className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-3"
                >
                    <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-slate-900">
                            {document.original_filename ?? 'Attached Document'}
                        </div>
                        <div className="text-xs text-slate-500">{document.media_type}</div>
                    </div>
                    <Button
                        asChild
                        variant="outline"
                        className="shrink-0 border-primary text-primary hover:bg-primary/5 hover:text-primary"
                    >
                        <a
                            href={`/api/portal/v1/payments/public/payment-request-links/documents/${document.payment_request_document_uuid}/signed-url`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View PDF
                        </a>
                    </Button>
                </div>
            ))}
        </div>
    );
}

export default function PaymentLinkPage() {
    const { token } = useParams<{ token?: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [bootstrapView, setBootstrapView] = useState<PublicPaymentRequestView | null>(null);
    const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
    const [bootstrapAttemptedToken, setBootstrapAttemptedToken] = useState<string | null>(null);
    const [cardFormData, setCardFormData] = useState<PaymentLinkCardFormData>({
        cardNumber: '',
        expiryDate: '',
        cvv: '',
        cardholderName: '',
        billingAddress: createEmptyUSBillingAddress(),
    });
    const [selectedStoredPaymentMethodUuid, setSelectedStoredPaymentMethodUuid] = useState<
        string | null
    >(null);
    const [paymentExecutionState, setPaymentExecutionState] =
        useState<PaymentExecutionState>('idle');
    const [paymentExecutionError, setPaymentExecutionError] =
        useState<PaymentExecutionErrorView | null>(null);
    const [paymentLinkActionNotice, setPaymentLinkActionNotice] =
        useState<PaymentLinkActionNotice | null>(null);
    const [receiptEmail, setReceiptEmail] = useState<string>('');
    const [storeCardForFutureUse, setStoreCardForFutureUse] = useState<boolean>(false);
    const [requiredBinLength, setRequiredBinLength] = useState<number>(6);
    const [resolvedCardType, setResolvedCardType] = useState<CardType | null>(null);
    const [isBinLookupLocked, setIsBinLookupLocked] = useState<boolean>(false);
    const [binLookupErrorMessage, setBinLookupErrorMessage] = useState<string | null>(null);
    const [cardFundingVerificationState, setCardFundingVerificationState] =
        useState<CardFundingVerificationState>('idle');
    const [hostedCardTokenResult, setHostedCardTokenResult] =
        useState<PortalHostedCardTokenResult | null>(null);
    const [hostedCardEntryTokenResult, setHostedCardEntryTokenResult] =
        useState<PortalHostedCardEntryTokenResult | null>(null);
    // One Pay Now: for entries that secure the card only on request, the click
    // asks for a token and the payment follows once it is quoted (Alex,
    // 2026-10-30), instead of a separate "Secure card details" step.
    const [hostedTokenizationRequestId, setHostedTokenizationRequestId] = useState<number>(0);
    const [hostedEntryTokenizationReady, setHostedEntryTokenizationReady] =
        useState<boolean>(false);
    const [payNowAwaitingToken, setPayNowAwaitingToken] = useState<boolean>(false);
    const binLookupCacheRef = useRef<Record<string, CardType>>({});
    const binLookupInFlightRef = useRef<boolean>(false);
    const receiptEmailInitializedPaymentRequestUuidRef = useRef<string | null>(null);

    const currentViewQuery = useQuery({
        queryKey: ['publicPaymentRequest', 'current'],
        queryFn: () => paymentsApi.getCurrentPublicPaymentRequest(),
        enabled: token === undefined,
        retry: false,
    });

    const bootstrapMutation = useMutation({
        mutationFn: (tokenValue: string) =>
            paymentsApi.bootstrapPaymentRequestGuestSession(tokenValue),
        onSuccess: (view) => {
            setLoadErrorMessage(null);
            if (view.state === 'ACTIVE') {
                queryClient.setQueryData(['publicPaymentRequest', 'current'], view);
                window.location.replace('/pay');
                return;
            }
            setBootstrapView(view);
        },
        onError: (error: unknown) => {
            setLoadErrorMessage(resolvePaymentRequestLoadErrorMessage(error));
        },
    });

    useEffect(() => {
        if (
            token !== undefined &&
            bootstrapAttemptedToken !== token &&
            !bootstrapMutation.isPending
        ) {
            setBootstrapAttemptedToken(token);
            bootstrapMutation.mutate(token);
        }
    }, [bootstrapAttemptedToken, bootstrapMutation, token]);

    const resolvedView: PublicPaymentRequestView | null = useMemo(() => {
        if (token !== undefined) {
            return bootstrapView;
        }
        return currentViewQuery.data ?? null;
    }, [bootstrapView, currentViewQuery.data, token]);

    const paymentRequestReadReady: boolean =
        resolvedView?.state === 'ACTIVE' &&
        (token === undefined
            ? currentViewQuery.isSuccess && !currentViewQuery.isFetching
            : bootstrapMutation.isSuccess && !bootstrapMutation.isPending);

    useEffect(() => {
        if (resolvedView?.state === 'PAID') {
            runInBackground(navigate('/pay/success', { replace: true }), () => {});
        }
    }, [navigate, resolvedView?.state]);

    const activePaymentRequestUuid: string | null =
        resolvedView?.state === 'ACTIVE' ? resolvedView.payment_request_uuid : null;

    const guestPaymentEntryContextQuery = useQuery({
        queryKey: ['publicPaymentRequest', 'paymentEntryContext', activePaymentRequestUuid],
        queryFn: () => paymentsApi.getCurrentGuestPaymentEntryContext(),
        enabled: activePaymentRequestUuid !== null,
        retry: false,
    });

    const guestDelegateTokenQuery = useQuery({
        queryKey: ['publicPaymentRequest', 'delegateToken', activePaymentRequestUuid],
        queryFn: () => paymentsApi.getCurrentGuestDelegateToken(),
        enabled:
            activePaymentRequestUuid !== null && guestPaymentEntryContextQuery.status === 'success',
        retry: false,
    });

    const authenticatedPaymentEntryContextQuery = useQuery<AuthenticatedPaymentEntryContextView>({
        queryKey: ['authenticatedPaymentRequest', 'paymentEntryContext', activePaymentRequestUuid],
        queryFn: () => paymentsApi.getCurrentAuthenticatedPaymentEntryContext(),
        enabled: activePaymentRequestUuid !== null,
        retry: false,
    });
    const selectableStoredPaymentMethods = filterSelectableStoredPaymentMethods(
        authenticatedPaymentEntryContextQuery.data?.stored_payment_methods ?? []
    );
    const portalCardEntryAdapter = useMemo(() => {
        const configuration = guestPaymentEntryContextQuery.data?.payment_configuration;
        return configuration === undefined
            ? null
            : resolvePortalCardEntryAdapter(
                  configuration.gateway_id,
                  configuration.card_details_model
              );
    }, [
        guestPaymentEntryContextQuery.data?.payment_configuration.card_details_model,
        guestPaymentEntryContextQuery.data?.payment_configuration.gateway_id,
    ]);

    useEffect(() => {
        const paymentEntryContext = guestPaymentEntryContextQuery.data;
        if (paymentEntryContext === undefined) {
            return;
        }
        if (
            receiptEmailInitializedPaymentRequestUuidRef.current ===
            paymentEntryContext.payment_request_uuid
        ) {
            return;
        }

        receiptEmailInitializedPaymentRequestUuidRef.current =
            paymentEntryContext.payment_request_uuid;
        setReceiptEmail(paymentEntryContext.receipt_email ?? '');
    }, [
        guestPaymentEntryContextQuery.data?.payment_request_uuid,
        guestPaymentEntryContextQuery.data?.receipt_email,
    ]);

    const selectedStoredPaymentMethod: CustomerPaymentMethodListItem | null = useMemo(() => {
        return (
            selectableStoredPaymentMethods.find(
                (paymentMethod) =>
                    paymentMethod.payment_method_token_uuid === selectedStoredPaymentMethodUuid
            ) ?? null
        );
    }, [selectableStoredPaymentMethods, selectedStoredPaymentMethodUuid]);

    useEffect(() => {
        if (selectedStoredPaymentMethodUuid === null) {
            return;
        }
        if (
            selectableStoredPaymentMethods.some(
                (paymentMethod) =>
                    paymentMethod.payment_method_token_uuid === selectedStoredPaymentMethodUuid
            )
        ) {
            return;
        }
        setSelectedStoredPaymentMethodUuid(null);
    }, [selectableStoredPaymentMethods, selectedStoredPaymentMethodUuid]);

    useEffect(() => {
        if (selectedStoredPaymentMethod !== null) {
            setResolvedCardType(resolveStoredPaymentMethodCardType(selectedStoredPaymentMethod));
            setBinLookupErrorMessage(null);
            setCardFundingVerificationState('resolved');
            return;
        }
        setRequiredBinLength(6);
        setResolvedCardType(null);
        setIsBinLookupLocked(false);
        setBinLookupErrorMessage(null);
        setCardFundingVerificationState('idle');
        binLookupCacheRef.current = {};
        binLookupInFlightRef.current = false;
    }, [
        selectedStoredPaymentMethod,
        guestPaymentEntryContextQuery.data?.payment_configuration.account_id,
        guestPaymentEntryContextQuery.data?.payment_configuration.credential_uuid,
    ]);

    useEffect(() => {
        if (
            hostedCardEntryTokenResult === null ||
            !isPortalHostedCardEntryAdapter(portalCardEntryAdapter)
        ) {
            return;
        }
        let isCurrentQuote = true;
        const quoteToken = async (): Promise<void> => {
            setCardFundingVerificationState('loading');
            setBinLookupErrorMessage(null);
            try {
                const billingAddress =
                    hostedCardEntryTokenResult.billingAddress ??
                    resolvePaymentLinkBillingAddress(cardFormData);
                const quote = await paymentsApi.guestHostedCardTokenQuote({
                    tokenization_result: hostedCardEntryTokenResult.tokenizationResult,
                    cardholder_name: hostedCardEntryTokenResult.cardholderName,
                    ...(billingAddress === undefined
                        ? {}
                        : {
                              billing_postal_code:
                                  billingAddress.country === 'US'
                                      ? billingAddress.zip
                                      : billingAddress.postal_code,
                          }),
                });
                if (!isCurrentQuote) return;
                if (quote.token_metadata_tier === HostedCardTokenMetadataTier.TOKEN_ONLY) {
                    setHostedCardTokenResult({
                        ...hostedCardEntryTokenResult,
                        tokenMetadataTier: HostedCardTokenMetadataTier.TOKEN_ONLY,
                    });
                    setResolvedCardType(CardType.UNKNOWN);
                    setCardFundingVerificationState('resolved');
                    return;
                }
                setHostedCardTokenResult({
                    ...hostedCardEntryTokenResult,
                    tokenMetadataTier: HostedCardTokenMetadataTier.METADATA_RICH,
                    panPrefix: resolveGatewayTokenizationPanPrefix(
                        hostedCardEntryTokenResult.tokenizationResult
                    ),
                    cardType: quote.card_type,
                    cardBrand: quote.card_brand,
                    cardLast4: quote.card_last4,
                    surchargeAmount: quote.surcharge_amount,
                });
                setResolvedCardType(quote.card_type);
                setCardFundingVerificationState('resolved');
            } catch (quoteError: unknown) {
                if (!isCurrentQuote) return;
                const message = resolveErrorMessage(quoteError);
                setPayNowAwaitingToken(false);
                setPaymentExecutionState('idle');
                setHostedCardTokenResult(null);
                setResolvedCardType(null);
                setCardFundingVerificationState('error');
                setBinLookupErrorMessage(message);
            }
        };
        runInBackground(quoteToken(), (quoteError: unknown) => {
            if (isCurrentQuote) {
                setBinLookupErrorMessage(resolveErrorMessage(quoteError));
            }
        });
        return (): void => {
            isCurrentQuote = false;
        };
    }, [cardFormData, hostedCardEntryTokenResult, portalCardEntryAdapter]);

    const receiptEmailValidationError = validateOptionalReceiptEmail(receiptEmail);

    useEffect(() => {
        const paymentEntryContext = guestPaymentEntryContextQuery.data;
        if (
            paymentEntryContext === undefined ||
            isBinLookupLocked ||
            selectedStoredPaymentMethod !== null ||
            isPortalHostedCardEntryAdapter(portalCardEntryAdapter)
        ) {
            return;
        }

        const cardNumberDigitsOnly: string =
            (hostedCardTokenResult?.tokenMetadataTier === HostedCardTokenMetadataTier.METADATA_RICH
                ? hostedCardTokenResult.panPrefix
                : undefined) ?? cardFormData.cardNumber.replace(/\D/g, '');
        if (cardNumberDigitsOnly.length < requiredBinLength) {
            setResolvedCardType(null);
            setCardFundingVerificationState('idle');
            return;
        }

        const panPrefixForLookup: string = cardNumberDigitsOnly.slice(0, requiredBinLength);
        const cachedCardType: CardType | undefined = binLookupCacheRef.current[panPrefixForLookup];
        if (cachedCardType !== undefined) {
            setResolvedCardType(cachedCardType);
            setBinLookupErrorMessage(null);
            setCardFundingVerificationState('resolved');
            return;
        }

        if (binLookupInFlightRef.current) {
            return;
        }

        const executeBinLookup = async (): Promise<void> => {
            binLookupInFlightRef.current = true;
            setCardFundingVerificationState('loading');
            setBinLookupErrorMessage(null);
            try {
                const lookupResponse = await paymentsApi.guestBinLookup({
                    pan_prefix: panPrefixForLookup,
                });
                binLookupCacheRef.current[panPrefixForLookup] = lookupResponse.card_type;
                setResolvedCardType(lookupResponse.card_type);
                setBinLookupErrorMessage(null);
                setCardFundingVerificationState('resolved');
            } catch (lookupError: unknown) {
                const lookupFailure = classifyCardFundingVerificationFailure(lookupError);
                if (lookupFailure.kind === CardFundingVerificationFailureKind.INVALID_BIN) {
                    if (requiredBinLength < 9) {
                        setRequiredBinLength(
                            (previousLength: number): number => previousLength + 1
                        );
                        setCardFundingVerificationState('idle');
                        return;
                    }
                    setIsBinLookupLocked(true);
                    setBinLookupErrorMessage(CARD_FUNDING_VERIFICATION_FAILED_MESSAGE);
                    setCardFundingVerificationState('error');
                    return;
                }
                const failure = resolveCardFundingVerificationFailure(lookupError);
                setIsBinLookupLocked(true);
                setBinLookupErrorMessage(failure.message);
                setCardFundingVerificationState(failure.state);
            } finally {
                binLookupInFlightRef.current = false;
            }
        };

        runInBackground(executeBinLookup(), (lookupError: unknown) => {
            const failure = resolveCardFundingVerificationFailure(lookupError);
            setIsBinLookupLocked(true);
            setBinLookupErrorMessage(failure.message);
            setCardFundingVerificationState(failure.state);
            binLookupInFlightRef.current = false;
        });
    }, [
        cardFormData.cardNumber,
        guestPaymentEntryContextQuery.data,
        hostedCardTokenResult,
        isBinLookupLocked,
        requiredBinLength,
        selectedStoredPaymentMethod,
        portalCardEntryAdapter,
    ]);

    const guestProcessCardTokenPaymentMutation = useMutation({
        mutationFn: async (): Promise<ProcessCardTokenPaymentResponse> => {
            if (!paymentRequestReadReady) {
                throw new Error('Refresh the payment request before making a payment.');
            }
            if (!guestPaymentEntryContextQuery.data || !guestDelegateTokenQuery.data) {
                throw new Error('Secure payment session is not ready.');
            }

            if (portalCardEntryAdapter?.model === CardDetailsModel.FULLY_OWNED) {
                const validationErrorMessage = validatePaymentLinkCardFormData(
                    cardFormData,
                    guestPaymentEntryContextQuery.data.payment_configuration
                        .addressRequiredForCardPayment !== false
                );
                if (validationErrorMessage !== null) {
                    throw new Error(validationErrorMessage);
                }
            } else if (hostedCardTokenResult === null) {
                throw new Error('Secure card entry is incomplete.');
            }
            if (resolvedCardType === null) {
                throw new Error('Card type has not been resolved yet.');
            }
            if (receiptEmailValidationError !== null) {
                throw new Error(receiptEmailValidationError);
            }

            let tokenResult: PortalHostedCardTokenResult;
            if (isPortalHostedCardEntryAdapter(portalCardEntryAdapter)) {
                if (hostedCardTokenResult === null) {
                    throw new Error('Secure card entry is incomplete.');
                }
                tokenResult = hostedCardTokenResult;
            } else {
                const refreshedDelegateToken = await paymentsApi.getCurrentGuestDelegateToken();
                const tokenizationResponse = await tokenizeCardWithDelegateToken({
                    cardData: cardFormData,
                    delegateToken: refreshedDelegateToken,
                });
                const normalizedPan = cardFormData.cardNumber.replace(/\D/g, '');
                tokenResult = {
                    token: resolveGatewayTokenizationPaymentToken(tokenizationResponse),
                    tokenMetadataTier: HostedCardTokenMetadataTier.METADATA_RICH,
                    panPrefix: normalizedPan.slice(0, 6),
                    cardType: resolvedCardType,
                    cardBrand: resolveGatewayTokenizationCardBrand({
                        response: tokenizationResponse,
                        fallbackCardBrand: resolveCardBrand(cardFormData.cardNumber),
                    }),
                    cardLast4: normalizedPan.slice(-4),
                    cardholderName: cardFormData.cardholderName.trim(),
                };
            }
            const paymentAmounts = resolvePaymentAmounts(
                guestPaymentEntryContextQuery.data,
                resolvedCardType,
                tokenResult.tokenMetadataTier === HostedCardTokenMetadataTier.METADATA_RICH
                    ? tokenResult.surchargeAmount
                    : undefined
            );
            if (paymentAmounts === null) {
                throw new Error('Payment totals are unavailable.');
            }
            const billingAddress = resolvePaymentLinkBillingAddress(cardFormData);
            const normalizedReceiptEmail = normalizeOptionalReceiptEmail(receiptEmail);
            const paymentRequest: GuestProcessCardTokenPaymentRequest = {
                ...(tokenResult.tokenizationResult === undefined
                    ? {}
                    : { tokenization_result: tokenResult.tokenizationResult }),
                token: tokenResult.token,
                ...(tokenResult.tokenMetadataTier === HostedCardTokenMetadataTier.TOKEN_ONLY
                    ? { token_metadata_tier: HostedCardTokenMetadataTier.TOKEN_ONLY }
                    : {
                          token_metadata_tier: HostedCardTokenMetadataTier.METADATA_RICH,
                          ...(tokenResult.panPrefix === undefined
                              ? {}
                              : { pan_prefix: tokenResult.panPrefix }),
                          card_brand: tokenResult.cardBrand,
                          card_last4: tokenResult.cardLast4,
                      }),
                cardholder_name: tokenResult.cardholderName,
                ...(tokenResult.expiryDate === undefined
                    ? {}
                    : { expiry_date: tokenResult.expiryDate }),
                ...(billingAddress === undefined ? {} : { billing_address: billingAddress }),
                ...(normalizedReceiptEmail === undefined
                    ? {}
                    : { receipt_email: normalizedReceiptEmail }),
                ...paymentAmounts,
            };

            if (authenticatedPaymentEntryContextQuery.isSuccess) {
                return await paymentsApi.processCurrentAuthenticatedCardTokenPayment({
                    ...paymentRequest,
                    stored_for_future_use: storeCardForFutureUse,
                });
            }

            return await paymentsApi.processCurrentGuestCardTokenPayment(paymentRequest);
        },
        onMutate: () => {
            setPaymentExecutionState('processing');
            setPaymentExecutionError(null);
        },
        onSuccess: (result) => {
            if (!result.success) {
                setPaymentExecutionState('error');
                setPaymentExecutionError(buildPaymentDeclineErrorView(result.error));
                return;
            }
            setPaymentExecutionState('success');
            queryClient
                .invalidateQueries({
                    queryKey: ['publicPaymentRequest', 'current'],
                })
                .catch(() => {});
            runInBackground(navigate('/pay/success'), () => {});
        },
        onError: (error: unknown) => {
            setPaymentExecutionState('error');
            setPaymentExecutionError(buildPaymentProcessingErrorView(error));
        },
    });

    const storedPaymentMethodMutation = useMutation({
        mutationFn: async (): Promise<ProcessCardTokenPaymentResponse> => {
            if (!paymentRequestReadReady) {
                throw new Error('Refresh the payment request before making a payment.');
            }
            if (selectedStoredPaymentMethodUuid === null) {
                throw new Error('Select a stored payment method.');
            }
            if (receiptEmailValidationError !== null) {
                throw new Error(receiptEmailValidationError);
            }

            const normalizedReceiptEmail = normalizeOptionalReceiptEmail(receiptEmail);
            return await paymentsApi.processCurrentStoredPaymentMethodPayment({
                payment_method_token_uuid: selectedStoredPaymentMethodUuid,
                ...(normalizedReceiptEmail === undefined
                    ? {}
                    : { receipt_email: normalizedReceiptEmail }),
            });
        },
        onMutate: () => {
            setPaymentExecutionState('processing');
            setPaymentExecutionError(null);
        },
        onSuccess: (result) => {
            if (!result.success) {
                setPaymentExecutionState('error');
                setPaymentExecutionError(buildPaymentDeclineErrorView(result.error));
                return;
            }
            setPaymentExecutionState('success');
            queryClient
                .invalidateQueries({
                    queryKey: ['publicPaymentRequest', 'current'],
                })
                .catch(() => {});
            runInBackground(navigate('/pay/success'), () => {});
        },
        onError: (error: unknown) => {
            setPaymentExecutionState('error');
            setPaymentExecutionError(buildPaymentProcessingErrorView(error));
        },
    });

    const requestReplacementLinkMutation = useMutation({
        mutationFn: async () => await paymentsApi.requestCurrentPaymentRequestReplacementLink(),
        onSuccess: (publicPaymentLinkActionResponse) => {
            if (resolvedView?.state === 'EXPIRED') {
                const publicPaymentRequestView: PublicPaymentRequestView = {
                    ...resolvedView,
                    state: 'REISSUED',
                    replacement_payment_request_uuid:
                        publicPaymentLinkActionResponse.payment_request_uuid,
                };
                if (token !== undefined) {
                    setBootstrapView(publicPaymentRequestView);
                } else {
                    queryClient.setQueryData(
                        ['publicPaymentRequest', 'current'],
                        publicPaymentRequestView
                    );
                }
            }
            setPaymentLinkActionNotice({
                tone: 'success',
                title: 'New Link Sent',
                body: 'A new payment link has been sent to the saved recipient.',
            });
        },
        onError: (error: unknown) => {
            setPaymentLinkActionNotice({
                tone: 'error',
                title: 'Unable to Send New Link',
                body: resolveErrorMessage(error),
            });
        },
    });

    const resendReissuedLinkMutation = useMutation({
        mutationFn: async () => await paymentsApi.resendCurrentReissuedPaymentRequestLink(),
        onSuccess: () => {
            setPaymentLinkActionNotice({
                tone: 'success',
                title: 'Replacement Link Sent',
                body: 'The current payment link has been resent to the saved recipient.',
            });
        },
        onError: (error: unknown) => {
            setPaymentLinkActionNotice({
                tone: 'error',
                title: 'Unable to Resend Link',
                body: resolveErrorMessage(error),
            });
        },
    });

    const hostedEntryTokenizesOnPay: boolean =
        selectedStoredPaymentMethod === null &&
        isPortalHostedCardEntryAdapter(portalCardEntryAdapter) &&
        hostedEntryTokenizationReady;

    // Instrumentation, not a fix (BUG-337): Pay Now once stayed busy on QA with
    // blank card fields. This records what it was waiting for; it changes nothing.
    useEffect(() => {
        if (!payNowAwaitingToken) return;
        const timer = setTimeout(() => {
            payLog.warn('Pay Now is still securing the card', {
                hostedEntryTokenizationReady,
                hostedTokenizationRequestId,
                hasEntryToken: hostedCardEntryTokenResult !== null,
                hasQuotedToken: hostedCardTokenResult !== null,
                cardFundingVerificationState,
            });
        }, 20_000);
        return (): void => clearTimeout(timer);
    }, [
        payNowAwaitingToken,
        hostedEntryTokenizationReady,
        hostedTokenizationRequestId,
        hostedCardEntryTokenResult,
        hostedCardTokenResult,
        cardFundingVerificationState,
    ]);

    useEffect(() => {
        if (!payNowAwaitingToken || hostedCardTokenResult === null || resolvedCardType === null) {
            return;
        }
        setPayNowAwaitingToken(false);
        guestProcessCardTokenPaymentMutation.mutate();
    }, [
        payNowAwaitingToken,
        hostedCardTokenResult,
        resolvedCardType,
        guestProcessCardTokenPaymentMutation,
    ]);

    const loadState: LoadState = useMemo(() => {
        if (resolvedView !== null) {
            return 'ready';
        }
        if (loadErrorMessage !== null || currentViewQuery.error !== null) {
            return 'error';
        }
        if (
            bootstrapMutation.isPending ||
            (token === undefined && currentViewQuery.isPending) ||
            resolvedView === null
        ) {
            return 'loading';
        }
        return 'ready';
    }, [
        bootstrapMutation.isPending,
        currentViewQuery.error,
        currentViewQuery.isPending,
        loadErrorMessage,
        resolvedView,
        token,
    ]);

    const loadFailure =
        loadErrorMessage !== null || currentViewQuery.error !== null ? (
            <div role="alert" className="mt-2 text-sm text-rose-700">
                <p>
                    {loadErrorMessage ??
                        resolvePaymentRequestLoadErrorMessage(currentViewQuery.error)}
                </p>
                <Button
                    type="button"
                    disabled={bootstrapMutation.isPending || currentViewQuery.isFetching}
                    onClick={() => {
                        if (token !== undefined) {
                            setLoadErrorMessage(null);
                            bootstrapMutation.mutate(token);
                        } else {
                            runInBackground(
                                queryClient.refetchQueries({
                                    queryKey: ['publicPaymentRequest'],
                                    type: 'active',
                                }),
                                () => {}
                            );
                        }
                    }}
                >
                    Retry
                </Button>
            </div>
        ) : null;

    if (loadState === 'loading') {
        return (
            <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
                <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h1 className="text-2xl font-semibold text-slate-900">
                        Loading payment request
                    </h1>
                    <p className="mt-2 text-sm text-slate-600">
                        Establishing secure access to the payment request.
                    </p>
                </div>
            </main>
        );
    }

    if (loadState === 'error') {
        return (
            <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
                <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h1 className="text-2xl font-semibold text-slate-900">
                        Payment Request Unavailable
                    </h1>
                    {loadFailure}
                    <div className="mt-6">
                        <Button asChild>
                            <Link to="/">Return Home</Link>
                        </Button>
                    </div>
                </div>
            </main>
        );
    }

    if (resolvedView === null) {
        throw new Error('Payment link state resolved without a view');
    }

    const formattedSourceDocumentReference = isResolvedPaymentRequestView(resolvedView)
        ? formatPaymentRequestSourceDocumentReference({
              sourceDocumentReference: resolvedView.source_document_reference,
              sourceDocumentKey: resolvedView.source_document_key,
          })
        : null;
    const storedPaymentMethods = selectableStoredPaymentMethods;
    const storedPaymentMethodDisplayValues = resolveStoredPaymentMethodDisplayValues(
        selectedStoredPaymentMethod
    );
    const displayedCardFormData = storedPaymentMethodDisplayValues ?? cardFormData;
    const addressRequiredForCardPayment =
        guestPaymentEntryContextQuery.data?.payment_configuration.addressRequiredForCardPayment !==
        false;
    const cardFormValidationError =
        selectedStoredPaymentMethod === null
            ? portalCardEntryAdapter?.model === CardDetailsModel.FULLY_OWNED
                ? validatePaymentLinkCardFormData(cardFormData, addressRequiredForCardPayment)
                : hostedCardTokenResult === null && !hostedEntryTokenizesOnPay
                  ? 'Secure card entry is incomplete.'
                  : null
            : null;
    const cardBrand =
        selectedStoredPaymentMethod !== null &&
        selectedStoredPaymentMethod.payment_method_token_details.payment_rail !== 'ACH'
            ? selectedStoredPaymentMethod.payment_method_token_details.card_brand
            : hostedCardTokenResult?.tokenMetadataTier === HostedCardTokenMetadataTier.METADATA_RICH
              ? hostedCardTokenResult.cardBrand
              : resolveCardBrand(cardFormData.cardNumber);
    const paymentSessionReady =
        paymentRequestReadReady &&
        guestPaymentEntryContextQuery.isSuccess &&
        guestDelegateTokenQuery.isSuccess;
    const paymentSessionHasError =
        guestPaymentEntryContextQuery.isError || guestDelegateTokenQuery.isError;
    const hasOptionalAuthenticatedPaymentEntryContextAuthorizationError =
        isOptionalAuthenticatedPaymentEntryContextAuthorizationError(
            authenticatedPaymentEntryContextQuery.error
        );
    const authenticatedPaymentEntryContextErrorMessage =
        hasOptionalAuthenticatedPaymentEntryContextAuthorizationError
            ? null
            : authenticatedPaymentEntryContextQuery.error !== null
              ? resolveErrorMessage(authenticatedPaymentEntryContextQuery.error)
              : null;
    const effectiveCardType =
        selectedStoredPaymentMethod !== null
            ? resolveStoredPaymentMethodCardType(selectedStoredPaymentMethod)
            : resolvedCardType;
    const paymentAmounts =
        guestPaymentEntryContextQuery.data === undefined
            ? null
            : resolvePaymentAmounts(
                  guestPaymentEntryContextQuery.data,
                  effectiveCardType,
                  hostedCardTokenResult?.tokenMetadataTier ===
                      HostedCardTokenMetadataTier.METADATA_RICH
                      ? hostedCardTokenResult.surchargeAmount
                      : undefined
              );
    const surchargePercent =
        guestPaymentEntryContextQuery.data?.payment_configuration.surcharge_pct ?? null;
    const hostedCardTypeIsUnresolved =
        selectedStoredPaymentMethod === null &&
        isPortalHostedCardEntryAdapter(portalCardEntryAdapter) &&
        (effectiveCardType === null || effectiveCardType === CardType.UNKNOWN);
    const shouldShowSurchargeDisclosure =
        surchargePercent !== null &&
        paymentAmounts !== null &&
        compareLosslessNumber(paymentAmounts.surcharge_amount, new LosslessNumber('0')) > 0 &&
        !hostedCardTypeIsUnresolved;
    const shouldShowUnknownCardSurchargeDisclosure =
        surchargePercent !== null &&
        (effectiveCardType === CardType.UNKNOWN || hostedCardTypeIsUnresolved);
    const transactionDetailsNotes = guestPaymentEntryContextQuery.data?.transaction_details.notes;
    const manualCardFundingVerificationMessage =
        selectedStoredPaymentMethod !== null
            ? null
            : cardFundingVerificationState === 'loading'
              ? isPortalHostedCardEntryAdapter(portalCardEntryAdapter)
                  ? null
                  : 'Verifying whether this card is credit or debit.'
              : binLookupErrorMessage;
    const payButtonDisabledTitle = !paymentRequestReadReady
        ? 'Refresh the payment request before making a payment.'
        : !paymentSessionReady
          ? 'Secure payment session is still being prepared.'
          : paymentSessionHasError
            ? 'Secure payment session is unavailable.'
            : selectedStoredPaymentMethod === null && cardFundingVerificationState === 'loading'
              ? 'Card funding source is being verified.'
              : selectedStoredPaymentMethod === null && isBinLookupLocked
                ? (binLookupErrorMessage ?? 'Card funding source must be verified before payment.')
                : cardFormValidationError !== null
                  ? cardFormValidationError
                  : receiptEmailValidationError !== null
                    ? receiptEmailValidationError
                    : selectedStoredPaymentMethod === null &&
                        resolvedCardType === null &&
                        !hostedEntryTokenizesOnPay
                      ? (binLookupErrorMessage ??
                        'Card funding source must be verified before payment.')
                      : paymentExecutionState === 'processing'
                        ? 'Payment is processing.'
                        : undefined;
    const heading = renderViewHeading(resolvedView);

    return (
        <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
            <div className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <>
                    <h1 className="text-2xl font-semibold text-slate-900">{heading.title}</h1>
                    <p className="mt-2 text-sm text-slate-600">{heading.body}</p>
                    {loadFailure}
                </>

                {isResolvedPaymentRequestView(resolvedView) ? (
                    <div className="mt-6 space-y-6">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                            <div className="text-sm font-medium uppercase tracking-wide text-slate-500">
                                Merchant
                            </div>
                            <div className="mt-2 text-xl font-semibold text-slate-900">
                                {resolvedView.merchant_display_name}
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="rounded-xl border border-slate-200 bg-white p-5">
                                <div className="text-sm font-medium uppercase tracking-wide text-slate-500">
                                    Amount Due
                                </div>
                                <div className="mt-2 text-3xl font-semibold text-slate-900">
                                    {formatCurrency(
                                        resolvedView.original_amount ??
                                            resolvedView.amount_breakdown?.total_amount,
                                        resolvedView.currency_code
                                    )}
                                </div>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-white p-5">
                                <div className="text-sm font-medium uppercase tracking-wide text-slate-500">
                                    Reference
                                </div>
                                <div className="mt-2 text-base font-medium text-slate-900">
                                    {formattedSourceDocumentReference ?? 'Unavailable'}
                                </div>
                            </div>
                        </div>

                        {transactionDetailsNotes !== undefined ? (
                            <div className="rounded-xl border border-slate-200 bg-white p-5">
                                <div className="text-sm font-medium uppercase tracking-wide text-slate-500">
                                    Notes
                                </div>
                                <div className="mt-2 whitespace-pre-wrap text-base text-slate-900">
                                    {transactionDetailsNotes}
                                </div>
                            </div>
                        ) : null}

                        {renderResolvedStateNote(resolvedView)}

                        {paymentLinkActionNotice !== null ? (
                            <div
                                className={
                                    paymentLinkActionNotice.tone === 'success'
                                        ? 'rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900'
                                        : 'rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700'
                                }
                            >
                                <div className="font-medium">{paymentLinkActionNotice.title}</div>
                                <div className="mt-1">{paymentLinkActionNotice.body}</div>
                            </div>
                        ) : null}

                        {resolvedView.state === 'EXPIRED' ? (
                            <div className="flex flex-wrap gap-3">
                                <Button
                                    type="button"
                                    disabled={requestReplacementLinkMutation.isPending}
                                    onClick={() => {
                                        setPaymentLinkActionNotice(null);
                                        requestReplacementLinkMutation.mutate();
                                    }}
                                >
                                    {requestReplacementLinkMutation.isPending
                                        ? 'Sending New Link...'
                                        : 'Request New Link'}
                                </Button>
                            </div>
                        ) : null}

                        {resolvedView.state === 'REISSUED' ? (
                            <div className="flex flex-wrap gap-3">
                                <Button
                                    type="button"
                                    disabled={resendReissuedLinkMutation.isPending}
                                    onClick={() => {
                                        setPaymentLinkActionNotice(null);
                                        resendReissuedLinkMutation.mutate();
                                    }}
                                >
                                    {resendReissuedLinkMutation.isPending
                                        ? 'Resending Link...'
                                        : 'Resend Current Link'}
                                </Button>
                            </div>
                        ) : null}

                        {resolvedView.state === 'ACTIVE' && (
                            <section className="space-y-3">
                                <h2 className="text-lg font-semibold text-slate-900">
                                    Payment Card Details
                                </h2>
                                {guestPaymentEntryContextQuery.isPending ||
                                guestDelegateTokenQuery.isPending ? (
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                                        Preparing secure payment-entry state.
                                    </div>
                                ) : null}
                                {guestPaymentEntryContextQuery.isError ||
                                guestDelegateTokenQuery.isError ? (
                                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                                        Unable to prepare secure payment entry.
                                    </div>
                                ) : null}
                                {guestPaymentEntryContextQuery.data !== undefined &&
                                guestDelegateTokenQuery.data !== undefined ? (
                                    <div className="space-y-4">
                                        {guestPaymentEntryContextQuery.data.login_cta !==
                                            undefined &&
                                        hasOptionalAuthenticatedPaymentEntryContextAuthorizationError ? (
                                            <div className="flex items-center justify-between gap-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                                                <div>
                                                    Recognized customer email:{' '}
                                                    <span className="font-medium">
                                                        {
                                                            guestPaymentEntryContextQuery.data
                                                                .login_cta.email
                                                        }
                                                    </span>
                                                </div>
                                                <Button asChild variant="outline">
                                                    <Link
                                                        to={
                                                            guestPaymentEntryContextQuery.data
                                                                .login_cta.href
                                                        }
                                                    >
                                                        Log in
                                                    </Link>
                                                </Button>
                                            </div>
                                        ) : null}

                                        <div className="space-y-2">
                                            <label className="space-y-2 text-sm font-medium text-slate-700">
                                                Receipt Email
                                                <input
                                                    type="email"
                                                    value={receiptEmail}
                                                    onChange={(event) => {
                                                        setReceiptEmail(event.target.value);
                                                    }}
                                                    placeholder="customer@example.com"
                                                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                                />
                                            </label>
                                            <p className="text-xs text-slate-600">
                                                We will email the receipt to this address after
                                                payment.
                                            </p>
                                            {receiptEmailValidationError !== null ? (
                                                <p className="text-xs text-rose-700">
                                                    {receiptEmailValidationError}
                                                </p>
                                            ) : null}
                                        </div>

                                        {authenticatedPaymentEntryContextQuery.isSuccess ? (
                                            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                                                <div className="text-sm font-medium text-slate-900">
                                                    Stored Payment Method
                                                </div>
                                                <select
                                                    aria-label="Stored payment method"
                                                    value={selectedStoredPaymentMethodUuid ?? ''}
                                                    onChange={(event) => {
                                                        const nextValue = event.target.value.trim();
                                                        setSelectedStoredPaymentMethodUuid(
                                                            nextValue.length > 0 ? nextValue : null
                                                        );
                                                        setPaymentExecutionState('idle');
                                                        setPaymentExecutionError(null);
                                                    }}
                                                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                                                >
                                                    <option value="">Use a new card</option>
                                                    {storedPaymentMethods.map((paymentMethod) => {
                                                        const details =
                                                            paymentMethod.payment_method_token_details;
                                                        const description =
                                                            details.payment_rail === 'ACH'
                                                                ? 'ACH'
                                                                : `${details.card_brand} ${details.masked_pan}`;
                                                        return (
                                                            <option
                                                                key={
                                                                    paymentMethod.payment_method_token_uuid
                                                                }
                                                                value={
                                                                    paymentMethod.payment_method_token_uuid
                                                                }
                                                            >
                                                                {description}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                {storedPaymentMethods.length === 0 ? (
                                                    <div className="text-sm text-slate-600">
                                                        No stored payment methods are available for
                                                        this customer account yet.
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        {authenticatedPaymentEntryContextErrorMessage !== null ? (
                                            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                                                {authenticatedPaymentEntryContextErrorMessage}
                                            </div>
                                        ) : null}

                                        {authenticatedPaymentEntryContextQuery.isSuccess &&
                                        selectedStoredPaymentMethod === null ? (
                                            <label
                                                className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700"
                                                title={SAVE_CARD_FOR_FASTER_CHECKOUT_TOOLTIP}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={storeCardForFutureUse}
                                                    onChange={(event) => {
                                                        setStoreCardForFutureUse(
                                                            event.target.checked
                                                        );
                                                    }}
                                                    className="mt-0.5 h-4 w-4"
                                                />
                                                <span>{SAVE_CARD_FOR_FASTER_CHECKOUT_LABEL}</span>
                                            </label>
                                        ) : null}

                                        <div className="space-y-4">
                                            {selectedStoredPaymentMethod !== null ||
                                            portalCardEntryAdapter?.model ===
                                                CardDetailsModel.FULLY_OWNED ? (
                                                <FullyOwnedInvoiceCardEntry
                                                    value={displayedCardFormData}
                                                    disabled={selectedStoredPaymentMethod !== null}
                                                    onChange={(nextCardData) => {
                                                        if (selectedStoredPaymentMethod !== null) {
                                                            return;
                                                        }
                                                        setHostedCardTokenResult(null);
                                                        setCardFormData((current) => ({
                                                            ...current,
                                                            ...nextCardData,
                                                        }));
                                                    }}
                                                />
                                            ) : isPortalHostedCardEntryAdapter(
                                                  portalCardEntryAdapter
                                              ) ? (
                                                <HostedInvoiceCardEntry
                                                    adapter={portalCardEntryAdapter}
                                                    delegateToken={guestDelegateTokenQuery.data}
                                                    disabled={
                                                        paymentExecutionState === 'processing'
                                                    }
                                                    tokenizationRequestId={
                                                        hostedTokenizationRequestId
                                                    }
                                                    onTokenizationReadyChange={
                                                        setHostedEntryTokenizationReady
                                                    }
                                                    onTokenized={(result) => {
                                                        setHostedCardEntryTokenResult(result);
                                                        setHostedCardTokenResult(null);
                                                        setIsBinLookupLocked(false);
                                                        setPaymentExecutionError(null);
                                                    }}
                                                    onError={(message) => {
                                                        setPayNowAwaitingToken(false);
                                                        setHostedCardEntryTokenResult(null);
                                                        setHostedCardTokenResult(null);
                                                        setPaymentExecutionState('error');
                                                        setPaymentExecutionError({
                                                            kind: 'processing',
                                                            title: 'Unable to secure card details',
                                                            body: message,
                                                        });
                                                    }}
                                                />
                                            ) : null}

                                            <div className="border-t border-slate-200 pt-4">
                                                <h3 className="text-base font-semibold text-slate-900">
                                                    Billing Address
                                                </h3>

                                                <div className="mt-4 grid gap-4 md:grid-cols-2">
                                                    <label className="space-y-2 text-sm font-medium text-slate-700">
                                                        Billing Country
                                                        <select
                                                            value={
                                                                displayedCardFormData.billingAddress
                                                                    .country
                                                            }
                                                            onChange={(event) => {
                                                                if (
                                                                    selectedStoredPaymentMethod !==
                                                                    null
                                                                ) {
                                                                    return;
                                                                }
                                                                setCardFormData((current) => ({
                                                                    ...current,
                                                                    billingAddress:
                                                                        changeBillingAddressCountry(
                                                                            current.billingAddress,
                                                                            event.target.value ===
                                                                                'CA'
                                                                                ? 'CA'
                                                                                : 'US'
                                                                        ),
                                                                }));
                                                            }}
                                                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                                            disabled={
                                                                selectedStoredPaymentMethod !== null
                                                            }
                                                        >
                                                            <option value="US">
                                                                United States
                                                            </option>
                                                            <option value="CA">Canada</option>
                                                        </select>
                                                    </label>

                                                    <label className="space-y-2 text-sm font-medium text-slate-700">
                                                        Billing Street
                                                        <input
                                                            type="text"
                                                            value={
                                                                displayedCardFormData.billingAddress
                                                                    .street
                                                            }
                                                            onChange={(event) => {
                                                                if (
                                                                    selectedStoredPaymentMethod !==
                                                                    null
                                                                ) {
                                                                    return;
                                                                }
                                                                setCardFormData((current) => ({
                                                                    ...current,
                                                                    billingAddress:
                                                                        updateBillingAddress(
                                                                            current.billingAddress,
                                                                            'street',
                                                                            event.target.value
                                                                        ),
                                                                }));
                                                            }}
                                                            placeholder="Billing street"
                                                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                                            readOnly={
                                                                selectedStoredPaymentMethod !== null
                                                            }
                                                        />
                                                    </label>

                                                    <label className="space-y-2 text-sm font-medium text-slate-700">
                                                        Billing Street 2
                                                        <input
                                                            type="text"
                                                            value={
                                                                displayedCardFormData.billingAddress
                                                                    .street2 ?? ''
                                                            }
                                                            onChange={(event) => {
                                                                if (
                                                                    selectedStoredPaymentMethod !==
                                                                    null
                                                                ) {
                                                                    return;
                                                                }
                                                                setCardFormData((current) => ({
                                                                    ...current,
                                                                    billingAddress:
                                                                        updateBillingAddress(
                                                                            current.billingAddress,
                                                                            'street2',
                                                                            event.target.value
                                                                        ),
                                                                }));
                                                            }}
                                                            placeholder="Billing street 2"
                                                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                                            readOnly={
                                                                selectedStoredPaymentMethod !== null
                                                            }
                                                        />
                                                    </label>

                                                    <label className="space-y-2 text-sm font-medium text-slate-700">
                                                        Billing City
                                                        <input
                                                            type="text"
                                                            value={
                                                                displayedCardFormData.billingAddress
                                                                    .city
                                                            }
                                                            onChange={(event) => {
                                                                if (
                                                                    selectedStoredPaymentMethod !==
                                                                    null
                                                                ) {
                                                                    return;
                                                                }
                                                                setCardFormData((current) => ({
                                                                    ...current,
                                                                    billingAddress:
                                                                        updateBillingAddress(
                                                                            current.billingAddress,
                                                                            'city',
                                                                            event.target.value
                                                                        ),
                                                                }));
                                                            }}
                                                            placeholder="Billing city"
                                                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                                            readOnly={
                                                                selectedStoredPaymentMethod !== null
                                                            }
                                                        />
                                                    </label>

                                                    {displayedCardFormData.billingAddress
                                                        .country === 'US' ? (
                                                        <>
                                                            <label className="space-y-2 text-sm font-medium text-slate-700">
                                                                Billing State
                                                                <select
                                                                    value={
                                                                        displayedCardFormData
                                                                            .billingAddress.state
                                                                    }
                                                                    onChange={(event) => {
                                                                        if (
                                                                            selectedStoredPaymentMethod !==
                                                                            null
                                                                        ) {
                                                                            return;
                                                                        }
                                                                        setCardFormData(
                                                                            (current) => ({
                                                                                ...current,
                                                                                billingAddress:
                                                                                    updateBillingAddress(
                                                                                        current.billingAddress,
                                                                                        'state',
                                                                                        event.target
                                                                                            .value
                                                                                    ),
                                                                            })
                                                                        );
                                                                    }}
                                                                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                                                    disabled={
                                                                        selectedStoredPaymentMethod !==
                                                                        null
                                                                    }
                                                                >
                                                                    {USStateKeyList.map(
                                                                        (stateCode) => (
                                                                            <option
                                                                                key={stateCode}
                                                                                value={stateCode}
                                                                            >
                                                                                {stateCode}
                                                                            </option>
                                                                        )
                                                                    )}
                                                                </select>
                                                            </label>
                                                            <label className="space-y-2 text-sm font-medium text-slate-700">
                                                                Billing ZIP
                                                                <input
                                                                    type="text"
                                                                    value={
                                                                        displayedCardFormData
                                                                            .billingAddress.zip
                                                                    }
                                                                    onChange={(event) => {
                                                                        if (
                                                                            selectedStoredPaymentMethod !==
                                                                            null
                                                                        ) {
                                                                            return;
                                                                        }
                                                                        setCardFormData(
                                                                            (current) => ({
                                                                                ...current,
                                                                                billingAddress:
                                                                                    updateBillingAddress(
                                                                                        current.billingAddress,
                                                                                        'zip',
                                                                                        event.target
                                                                                            .value
                                                                                    ),
                                                                            })
                                                                        );
                                                                    }}
                                                                    placeholder="Billing ZIP"
                                                                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                                                    readOnly={
                                                                        selectedStoredPaymentMethod !==
                                                                        null
                                                                    }
                                                                />
                                                            </label>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <label className="space-y-2 text-sm font-medium text-slate-700">
                                                                Billing Province
                                                                <select
                                                                    value={
                                                                        displayedCardFormData
                                                                            .billingAddress.province
                                                                    }
                                                                    onChange={(event) => {
                                                                        if (
                                                                            selectedStoredPaymentMethod !==
                                                                            null
                                                                        ) {
                                                                            return;
                                                                        }
                                                                        setCardFormData(
                                                                            (current) => ({
                                                                                ...current,
                                                                                billingAddress:
                                                                                    updateBillingAddress(
                                                                                        current.billingAddress,
                                                                                        'province',
                                                                                        event.target
                                                                                            .value
                                                                                    ),
                                                                            })
                                                                        );
                                                                    }}
                                                                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                                                    disabled={
                                                                        selectedStoredPaymentMethod !==
                                                                        null
                                                                    }
                                                                >
                                                                    {CanadaProvinceKeyList.map(
                                                                        (provinceCode) => (
                                                                            <option
                                                                                key={provinceCode}
                                                                                value={provinceCode}
                                                                            >
                                                                                {provinceCode}
                                                                            </option>
                                                                        )
                                                                    )}
                                                                </select>
                                                            </label>
                                                            <label className="space-y-2 text-sm font-medium text-slate-700">
                                                                Billing Postal Code
                                                                <input
                                                                    type="text"
                                                                    value={
                                                                        displayedCardFormData
                                                                            .billingAddress
                                                                            .postal_code
                                                                    }
                                                                    onChange={(event) => {
                                                                        if (
                                                                            selectedStoredPaymentMethod !==
                                                                            null
                                                                        ) {
                                                                            return;
                                                                        }
                                                                        setCardFormData(
                                                                            (current) => ({
                                                                                ...current,
                                                                                billingAddress:
                                                                                    updateBillingAddress(
                                                                                        current.billingAddress,
                                                                                        'postal_code',
                                                                                        event.target
                                                                                            .value
                                                                                    ),
                                                                            })
                                                                        );
                                                                    }}
                                                                    placeholder="Billing postal code"
                                                                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                                                    readOnly={
                                                                        selectedStoredPaymentMethod !==
                                                                        null
                                                                    }
                                                                />
                                                            </label>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                                            <div className="flex items-center justify-between gap-4">
                                                <span>Detected card brand</span>
                                                <span className="font-medium text-slate-900">
                                                    {cardBrand}
                                                </span>
                                            </div>
                                            {paymentAmounts !== null ? (
                                                <>
                                                    {shouldShowSurchargeDisclosure ? (
                                                        <div className="mt-2 flex items-center justify-between gap-4">
                                                            <span>
                                                                Credit card surcharge{' '}
                                                                {formatSurchargePercent(
                                                                    surchargePercent
                                                                )}
                                                                %
                                                            </span>
                                                            <span className="font-medium text-slate-900">
                                                                {formatCurrency(
                                                                    paymentAmounts.total_fee_amount,
                                                                    guestPaymentEntryContextQuery
                                                                        .data.currency_code
                                                                )}
                                                            </span>
                                                        </div>
                                                    ) : null}
                                                    <div className="mt-2 flex items-center justify-between gap-4">
                                                        <span>Total to be charged</span>
                                                        <span className="font-medium text-slate-900">
                                                            {formatCurrency(
                                                                paymentAmounts.total_amount_paid,
                                                                guestPaymentEntryContextQuery.data
                                                                    .currency_code
                                                            )}
                                                        </span>
                                                    </div>
                                                </>
                                            ) : null}
                                        </div>

                                        {manualCardFundingVerificationMessage !== null ? (
                                            <div
                                                className={
                                                    cardFundingVerificationState === 'loading'
                                                        ? 'rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900'
                                                        : 'rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700'
                                                }
                                            >
                                                {manualCardFundingVerificationMessage}
                                            </div>
                                        ) : null}

                                        {paymentExecutionState === 'error' &&
                                        paymentExecutionError !== null ? (
                                            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                                                <div className="font-medium text-rose-900">
                                                    {paymentExecutionError.title}
                                                </div>
                                                <div className="mt-1">
                                                    {paymentExecutionError.body}
                                                </div>
                                            </div>
                                        ) : null}

                                        <Button
                                            type="button"
                                            disabled={
                                                !paymentSessionReady ||
                                                paymentSessionHasError ||
                                                cardFormValidationError !== null ||
                                                receiptEmailValidationError !== null ||
                                                (selectedStoredPaymentMethod === null
                                                    ? isBinLookupLocked ||
                                                      (resolvedCardType === null &&
                                                          !hostedEntryTokenizesOnPay)
                                                    : false) ||
                                                paymentExecutionState === 'processing' ||
                                                payNowAwaitingToken
                                            }
                                            title={payButtonDisabledTitle}
                                            onClick={() => {
                                                if (selectedStoredPaymentMethod !== null) {
                                                    storedPaymentMethodMutation.mutate();
                                                    return;
                                                }
                                                if (hostedEntryTokenizesOnPay) {
                                                    // Secure the card first; the payment follows
                                                    // once its token is quoted.
                                                    setPaymentExecutionError(null);
                                                    setHostedCardEntryTokenResult(null);
                                                    setHostedCardTokenResult(null);
                                                    setResolvedCardType(null);
                                                    setPayNowAwaitingToken(true);
                                                    setHostedTokenizationRequestId(
                                                        (requestId: number): number => requestId + 1
                                                    );
                                                    return;
                                                }
                                                guestProcessCardTokenPaymentMutation.mutate();
                                            }}
                                        >
                                            {payNowAwaitingToken
                                                ? 'Securing card details...'
                                                : paymentExecutionState === 'processing'
                                                  ? 'Processing Payment...'
                                                  : 'Pay Now'}
                                        </Button>
                                        {shouldShowSurchargeDisclosure ? (
                                            <p className="text-xs text-slate-600">
                                                A surcharge of{' '}
                                                {formatSurchargePercent(surchargePercent)}% is
                                                applied to credit card transactions. This surcharge
                                                is not greater than our cost of acceptance. Debit
                                                card transactions are not subject to a surcharge.
                                            </p>
                                        ) : shouldShowUnknownCardSurchargeDisclosure ? (
                                            <p className="text-xs text-slate-600">
                                                If the payment method is a credit card an{' '}
                                                {formatSurchargePercent(surchargePercent)}%
                                                surcharge is applied. No surcharge is applied for
                                                debit cards.
                                            </p>
                                        ) : null}
                                        <p className="text-xs text-slate-600">
                                            By clicking &quot;Pay Now&quot; you are accepting
                                            Anchorbase&apos;s Terms of Service and Privacy Policy.
                                        </p>
                                    </div>
                                ) : null}
                            </section>
                        )}

                        <section className="space-y-3">
                            <h2 className="text-lg font-semibold text-slate-900">Documents</h2>
                            {renderDocumentActions(resolvedView)}
                        </section>
                    </div>
                ) : (
                    <div className="mt-6 space-y-6">
                        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                            {heading.body}
                        </div>
                        <div>
                            <Button asChild>
                                <Link to="/">Return Home</Link>
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
}
